import axios from "axios";
import { ERROR_MSGS } from "../constants/constants";

const apiUrl = process.env.REACT_APP_API_URL;
const AUTH_ROLE = "ADMIN";
// Refresh before the access cookie expires (API access TTL is 30 minutes).
const KEEP_ALIVE_INTERVAL_MS = 20 * 60 * 1000;

const isSessionRevokedError = (err) => {
  const raw =
    err?.response?.data?.error?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.response ||
    err?.message ||
    "";
  const text = typeof raw === "string" ? raw : raw?.message || "";
  return /session revoked/i.test(text);
};

let api;
if (typeof axios.create === "function") {
  const created = axios.create({
    baseURL: apiUrl,
    withCredentials: true,
    headers: {
      "Content-Type": "application/json",
    },
  });
  api = created || {
    interceptors: { request: { use: () => {} }, response: { use: () => {} } },
    get: () => Promise.resolve({}),
    post: () => Promise.resolve({}),
    put: () => Promise.resolve({}),
    defaults: { headers: {} },
  };
} else {
  api = {
    interceptors: { request: { use: () => {} }, response: { use: () => {} } },
    get: () => Promise.resolve({}),
    post: () => Promise.resolve({}),
    put: () => Promise.resolve({}),
    defaults: { headers: {} },
  };
}

// Ensure interceptors exist even if axios.create returned an object missing them
if (!api.interceptors) {
  api.interceptors = { request: { use: () => {} }, response: { use: () => {} } };
}

// Ensure methods exist
api.get = api.get || (() => Promise.resolve({}));
api.post = api.post || (() => Promise.resolve({}));
api.put = api.put || (() => Promise.resolve({}));

let refreshPromise = null;
let keepAliveTimer = null;

const isRefreshRequest = (config) => {
  const url = config?.url || "";
  return url.includes("/api/auth/refresh");
};

const getErrorMessage = (error) =>
  error.response?.data?.error ||
  error.response?.data?.message ||
  error.response?.data?.response ||
  "An error occurred";

export const refreshSession = () => {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${apiUrl}/api/auth/refresh`, { role: AUTH_ROLE }, { withCredentials: true })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

const onVisibilityChange = () => {
  if (typeof document !== "undefined" && document.visibilityState === "visible") {
    refreshSession().catch(() => {});
  }
};

export const startSessionKeepAlive = () => {
  if (typeof window === "undefined" || keepAliveTimer) return;
  refreshSession().catch(() => {});
  keepAliveTimer = setInterval(() => {
    refreshSession().catch(() => {});
  }, KEEP_ALIVE_INTERVAL_MS);
  document.addEventListener("visibilitychange", onVisibilityChange);
};

export const stopSessionKeepAlive = () => {
  if (keepAliveTimer) {
    clearInterval(keepAliveTimer);
    keepAliveTimer = null;
  }
  if (typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", onVisibilityChange);
  }
};

const forceLogout = (sessionRevoked = false) => {
  stopSessionKeepAlive();
  localStorage.removeItem("user");
  sessionStorage.removeItem("admin_session_active");
  if (sessionRevoked) {
    sessionStorage.setItem(
      "sessionRevokedMessage",
      ERROR_MSGS.SESSION_ELSEWHERE
    );
  }
  window.location.href = "/";
};

// expose handler refs for tests
let _reqSuccess;
let _reqError;
let _resSuccess;
let _resError;

// Register request interceptor and expose handlers for tests
if (api.interceptors && api.interceptors.request && typeof api.interceptors.request.use === "function") {
  _reqSuccess = (config) => config;
  _reqError = (error) => Promise.reject(error);
  try {
    api.interceptors.request._success = _reqSuccess;
    api.interceptors.request._error = _reqError;
  } catch (e) {}
  api.interceptors.request.use(_reqSuccess, _reqError);
}

// Register response interceptor and expose handlers for tests
if (api.interceptors && api.interceptors.response && typeof api.interceptors.response.use === "function") {
  _resSuccess = (response) => response.data;
  _resError = async (error) => {
    const originalRequest = error.config;

    if (error.response?.data?.isLinkExpired) {
      return Promise.reject(error.response.data);
    }

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isRefreshRequest(originalRequest)
    ) {
      originalRequest._retry = true;

      try {
        await refreshSession();
        return api(originalRequest);
      } catch (err) {
        forceLogout(isSessionRevokedError(err));
        return Promise.reject(err);
      }
    }

    return Promise.reject(getErrorMessage(error));
  };
  try {
    api.interceptors.response._success = _resSuccess;
    api.interceptors.response._error = _resError;
  } catch (e) {}
  api.interceptors.response.use(_resSuccess, _resError);
}

// attach test handlers object for tests to invoke directly
try {
  api.__testHandlers = {
    request: { success: _reqSuccess, error: _reqError },
    response: { success: _resSuccess, error: _resError },
  };
} catch (e) {}

export default api;
